import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

// Get auth service configuration
const getAuthConfig = () => ({
  url:
    window.ENV?.REACT_APP_AUTH_URL ||
    process.env.REACT_APP_AUTH_URL ||
    "https://auth.beilke.com.br/api",
  apiKey:
    window.ENV?.REACT_APP_AUTH_API_KEY ||
    process.env.REACT_APP_AUTH_API_KEY ||
    "",
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check URL params for token first (for cross-domain redirects)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get("token");

    if (urlToken) {
      console.log("[AuthContext] Found token in URL, storing in localStorage");
      localStorage.setItem("token", urlToken);
      // Remove token from URL for security
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const token = localStorage.getItem("token");
    console.log("[AuthContext] checkAuth - Token exists:", !!token);

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const { url, apiKey } = getAuthConfig();
      console.log(
        "[AuthContext] Fetching user from:",
        `${url}/tokens/user`,
      );
      console.log(
        "[AuthContext] Using API Key:",
        apiKey ? "Present" : "Missing",
      );

      const response = await fetch(`${url}/tokens/user`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-Key": apiKey,
        },
      });

      console.log("[AuthContext] Response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("[AuthContext] Response data:", data);

        if (data.valid && data.user) {
          console.log("[AuthContext] User authenticated:", {
            email: data.user.email,
            roles: data.user.roles,
            permissions: data.user.permissions,
          });
          setUser(data.user);
        } else {
          console.warn("[AuthContext] Invalid response - no user data");
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
        }
      } else {
        const errorData = await response.text();
        console.error(
          "[AuthContext] Auth validation failed:",
          response.status,
          errorData,
        );
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
      }
    } catch (error) {
      console.error("[AuthContext] Auth check failed:", error);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    const { url, apiKey } = getAuthConfig();

    const response = await fetch(`${url}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || "Login failed");
    }

    const data = await response.json();

    // Check if MFA setup is required (app requires MFA but user hasn't set it up)
    if (data.mfaSetupRequired) {
      return {
        mfaSetupRequired: true,
        mfaSetupToken: data.mfaSetupToken,
        user: data.user,
        message: data.message,
      };
    }

    // Check if MFA verification is required
    // Auth service returns 'twoFactorRequired', normalize to 'mfaRequired'
    if (data.twoFactorRequired || data.mfaRequired) {
      return { mfaRequired: true, tempToken: data.tempToken };
    }

    localStorage.setItem("token", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    setUser(data.user);
    return data;
  };

  // Verify MFA code - re-calls login with the 2FA code
  // The auth service doesn't have a separate verify-2fa endpoint
  // Instead, we call login again with the twoFactorCode
  const verifyMfa = async (email, password, mfaCode) => {
    const { url, apiKey } = getAuthConfig();

    const response = await fetch(`${url}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
      body: JSON.stringify({
        email,
        password,
        twoFactorCode: mfaCode,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error || error.message || "MFA verification failed",
      );
    }

    const data = await response.json();

    // Should now have tokens
    if (!data.accessToken || !data.refreshToken) {
      throw new Error("MFA verification failed - no tokens received");
    }

    localStorage.setItem("token", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    const { url, apiKey } = getAuthConfig();
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");

    try {
      await fetch(`${url}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-Key": apiKey,
        },
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    verifyMfa,
    getAuthConfig,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
